import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useProfile } from '../../contexts/ProfileContext';
import { formatCombinedDifficulty } from '../../contexts/gameHelpers';
import SetUsernameModal from './SetUsernameModal';
import ConfirmationModal from '../ui/ConfirmationModal';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUserPlus, faEdit, faTimes, faCheck, faExclamationTriangle,
  faInfoCircle, faUser, faArrowRight, faChevronRight
} from '@fortawesome/free-solid-svg-icons';

// --- Configuration ---
const MAX_PROFILES_PER_USER = 6;
const DEBOUNCE_DELAY = 500; // Milliseconds for username availability check debounce

// --- Define Base URLs for Cloud Function ---
const deployedBaseUrl = "https://us-central1-dual-n-back-web-app.cloudfunctions.net";
const emulatorBaseUrl = "http://127.0.0.1:5001/dual-n-back-web-app/us-central1";
const isLocalDevelopment = typeof window !== 'undefined' && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
const functionBaseUrl = isLocalDevelopment ? emulatorBaseUrl : deployedBaseUrl;
const checkUsernameUrl = `${functionBaseUrl}/checkUsernameAvailability`;

/**
 * Displays a single profile card, handling selection and edit mode interactions.
 */
const ProfileCard = ({ profile, onSelect, isSelected, isEditMode, onDeleteRequest }) => {
  const displayLevel = formatCombinedDifficulty(profile.currentNBack, profile.currentDifficulty);
  const displayName = profile.username || `Profile ${profile.id.substring(0, 4)}`;
  const needsUsername = !profile.username;

  const handleCardClick = () => {
    if (!isEditMode) {
      onSelect(profile.id);
    }
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    onDeleteRequest(profile.id, displayName);
  };

  return (
    <div
      className={`relative border rounded-lg overflow-hidden transition-all duration-200 flex flex-col justify-between ${
        isSelected && !isEditMode
          ? 'border-indigo-500 ring-2 ring-indigo-400 shadow-lg bg-indigo-50'
          : 'border-gray-200 bg-white hover:border-indigo-300 hover:shadow'
      } ${
        isEditMode ? 'shadow-sm' : 'cursor-pointer'
      }`}
      onClick={handleCardClick}
      onKeyDown={(e) => !isEditMode && (e.key === 'Enter' || e.key === ' ') && handleCardClick()}
      role={!isEditMode ? "button" : undefined}
      tabIndex={!isEditMode ? 0 : -1}
      aria-pressed={isSelected && !isEditMode}
    >
      {/* Card Header with Delete Button */}
      <div className={`px-5 py-4 ${needsUsername ? 'bg-amber-50 border-b border-amber-200' : ''}`}>
        <div className="flex justify-between items-start">
          <div className="flex items-center">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-3 ${needsUsername ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'}`}>
              <FontAwesomeIcon icon={faUser} className="text-lg" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800 truncate" title={displayName}>
                {displayName}
              </h3>
              {needsUsername && (
                <span className="text-xs text-amber-600 flex items-center mt-0.5">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
                  Needs username setup
                </span>
              )}
            </div>
          </div>

          {isEditMode && (
            <button
              onClick={handleDeleteClick}
              className="group bg-red-100 text-red-600 rounded-md p-2 hover:bg-red-600 hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              aria-label={`Delete profile ${displayName}`}
              title="Delete Profile"
            >
              <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Card Body with Profile Info */}
      <div className="px-5 py-4 flex-grow">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-500">Current Level</span>
          <span className="text-lg font-bold text-indigo-700">{displayLevel}</span>
        </div>

        {profile.highestNBack > profile.currentNBack && (
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">Highest n-Back</span>
            <span className="text-sm font-medium text-gray-700">{profile.highestNBack}-back</span>
          </div>
        )}
      </div>

      {/* Card Footer with Action Hint */}
      <div className={`px-5 py-3 bg-gray-50 border-t border-gray-100 ${isEditMode ? 'opacity-50' : ''}`}>
        {!isEditMode ? (
          <div className="flex justify-between items-center text-sm">
            <span className={`${needsUsername ? 'text-amber-600' : 'text-indigo-600'}`}>
              {needsUsername ? 'Set username' : 'Select profile'}
            </span>
            <FontAwesomeIcon
              icon={faChevronRight}
              className={`${needsUsername ? 'text-amber-600' : 'text-indigo-600'}`}
            />
          </div>
        ) : (
          <div className="text-xs text-center text-gray-500">Edit mode active</div>
        )}
      </div>
    </div>
  );
};

/**
 * Loading spinner component
 */
const LoadingSpinner = ({ small = false }) => (
  <div className={`flex justify-center items-center ${small ? 'p-1' : 'p-4'}`}>
    <svg className={`animate-spin text-indigo-600 ${small ? 'h-4 w-4' : 'h-8 w-8'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  </div>
);

/**
 * Error message component
 */
const ErrorMessage = ({ message }) => (
  <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
    <div className="bg-white rounded-lg p-6 shadow-md max-w-md w-full border border-red-200">
      <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-red-100">
        <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600 text-xl" />
      </div>
      <h2 className="text-xl font-semibold text-gray-800 text-center mb-3">Error Loading Profiles</h2>
      <p className="text-red-600 text-center mb-4">{message || "An unknown error occurred loading profile data."}</p>
      <div className="flex justify-center">
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
        >
          Retry
        </button>
      </div>
    </div>
  </div>
);

/**
 * Main ProfileSelectionScreen Component
 */
const ProfileSelectionScreen = () => {
  // --- Context Data & Actions ---
  const {
    profiles,
    activeProfileId,
    selectProfile,
    addProfile,
    setProfileUsername,
    removeProfile,
    isLoading,
    error: profileError
  } = useProfile();

  const navigate = useNavigate();

  // --- Local State ---
  const [showAddForm, setShowAddForm] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [isAddingProfile, setIsAddingProfile] = useState(false);
  const [addProfileError, setAddProfileError] = useState(null);
  const [usernameValidationError, setUsernameValidationError] = useState('');
  const [isCheckingUsername, setIsCheckingUsername] = useState(false);
  const [usernameAvailability, setUsernameAvailability] = useState(null);
  const [debouncedUsernameCheckValue, setDebouncedUsernameCheckValue] = useState('');
  const [isSetUsernameModalOpen, setIsSetUsernameModalOpen] = useState(false);
  const [profileToMigrateId, setProfileToMigrateId] = useState(null);
  const [profileToMigrateData, setProfileToMigrateData] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [profileToDeleteId, setProfileToDeleteId] = useState(null);
  const [profileToDeleteName, setProfileToDeleteName] = useState('');
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState('');

  // Ref for debouncing username availability checks
  const debounceTimerRef = useRef(null);

  // --- Validation & Debouncing Logic ---
  const validateUsername = useCallback((username) => {
    const trimmed = username.trim();
    if (trimmed.length === 0) return '';
    if (trimmed.length < 3 || trimmed.length > 25) return "Username must be 3-25 characters long.";
    if (!/^[a-zA-Z0-9_-]+$/.test(trimmed)) return "Only letters, numbers, underscores (_), and dashes (-) allowed.";
    return '';
  }, []);

  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    const trimmedUsername = newUsername.trim();
    const validationError = validateUsername(trimmedUsername);
    setUsernameValidationError(validationError);
    setAddProfileError(null);
    setUsernameAvailability(null);

    if (validationError || trimmedUsername.length === 0) {
      setDebouncedUsernameCheckValue('');
      return;
    }

    debounceTimerRef.current = setTimeout(() => {
      setUsernameAvailability('checking');
      setDebouncedUsernameCheckValue(trimmedUsername);
    }, DEBOUNCE_DELAY);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [newUsername, validateUsername]);

  useEffect(() => {
    if (!debouncedUsernameCheckValue || usernameAvailability !== 'checking') {
      setIsCheckingUsername(false);
      return;
    }

    setIsCheckingUsername(true);
    let isCancelled = false;

    const checkAvailability = async () => {
      try {
        const response = await fetch(checkUsernameUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: debouncedUsernameCheckValue }),
        });

        if (isCancelled) return;

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || `Server error: ${response.status}`);
        }

        const data = await response.json();
        setUsernameAvailability(data.available ? 'available' : 'taken');
      } catch (error) {
        console.error("Error checking username:", error);
        if (!isCancelled) setUsernameAvailability('error');
      } finally {
        if (!isCancelled) setIsCheckingUsername(false);
      }
    };

    checkAvailability();

    return () => {
      isCancelled = true;
      setIsCheckingUsername(false);
    };
  }, [debouncedUsernameCheckValue, usernameAvailability]);

  // --- Event Handlers ---
  const handleSelectProfile = useCallback((profileId) => {
    const profile = profiles.find(p => p.id === profileId);
    if (!profile) {
      console.error(`Profile ${profileId} not found!`);
      return;
    }

    if (profile.username) {
      selectProfile(profileId);
      navigate('/dashboard');
    } else {
      setProfileToMigrateId(profileId);
      setProfileToMigrateData(profile);
      setIsSetUsernameModalOpen(true);
    }
  }, [profiles, selectProfile, navigate]);

  const handleAddProfileSubmit = useCallback(async (e) => {
    e.preventDefault();
    const trimmedUsername = newUsername.trim();
    const validationError = validateUsername(trimmedUsername);
    setUsernameValidationError(validationError);

    if (validationError || usernameAvailability !== 'available') {
      setAddProfileError(usernameAvailability === 'taken' ? 'Username taken.' : 'Invalid/unavailable username.');
      return;
    }

    if (Array.isArray(profiles) && profiles.length >= MAX_PROFILES_PER_USER) {
      setAddProfileError(`Max profiles (${MAX_PROFILES_PER_USER}) reached.`);
      return;
    }

    setAddProfileError(null);
    setIsAddingProfile(true);

    try {
      const newProfile = await addProfile({ username: trimmedUsername });
      if (newProfile) {
        // Reset form state and hide it
        setShowAddForm(false);
        setNewUsername('');
        setUsernameAvailability(null);
        setUsernameValidationError('');
        setAddProfileError(null);
        console.log(`Profile ${newProfile.id} created successfully. Staying on selection screen.`);
      } else {
        setAddProfileError(profileError || "Failed to create profile.");
        setUsernameAvailability('taken');
      }
    } catch (err) {
      console.error("Error creating profile:", err);
      setAddProfileError("Unexpected error.");
    } finally {
      setIsAddingProfile(false);
    }
  }, [newUsername, addProfile, profileError, profiles, usernameAvailability, validateUsername]);

  const handleCloseSetUsernameModal = useCallback(() => {
    setIsSetUsernameModalOpen(false);
    setProfileToMigrateId(null);
    setProfileToMigrateData(null);
  }, []);

  const handleSaveMigratedUsername = useCallback(async (usernameToSave) => {
    if (!profileToMigrateId || !setProfileUsername) return false;

    const success = await setProfileUsername(profileToMigrateId, usernameToSave);
    if (success) {
      handleCloseSetUsernameModal();
      selectProfile(profileToMigrateId);
      navigate('/dashboard');
      return true;
    }
    return false;
  }, [profileToMigrateId, setProfileUsername, navigate, handleCloseSetUsernameModal, selectProfile]);

  const handleDeleteRequest = useCallback((id, name) => {
    const profile = profiles.find(p => p.id === id);
    if (profile?.username) {
      setProfileToDeleteId(id);
      setProfileToDeleteName(profile.username);
      setDeleteConfirmInput('');
      setShowDeleteConfirmModal(true);
    } else {
      alert("Cannot delete this profile as it doesn't have a username set.");
      console.warn(`Attempted to delete profile ${id} which has no valid username.`);
    }
  }, [profiles]);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirmModal(false);
    setProfileToDeleteId(null);
    setProfileToDeleteName('');
    setDeleteConfirmInput('');
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!profileToDeleteId || !removeProfile || deleteConfirmInput.trim().toLowerCase() !== profileToDeleteName.toLowerCase()) {
      console.warn("Delete confirmation failed: ID missing, action missing, or input doesn't match.");
      alert(`Confirmation failed. Please ensure you type the username "${profileToDeleteName}" exactly.`);
      return;
    }

    setIsDeleting(true);
    setAddProfileError(null);

    try {
      const success = await removeProfile(profileToDeleteId);
      if (!success) {
        setAddProfileError(profileError || "Failed to delete profile.");
      }
    } catch (err) {
      console.error("Error deleting profile:", err);
      setAddProfileError("An unexpected error occurred during deletion.");
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmModal(false);
      setProfileToDeleteId(null);
      setProfileToDeleteName('');
      setDeleteConfirmInput('');
      if (isEditMode) setIsEditMode(false);
    }
  }, [profileToDeleteId, removeProfile, profileError, profileToDeleteName, deleteConfirmInput, isEditMode]);

  // --- Render Logic ---
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-100">
        <LoadingSpinner />
        <p className="mt-4 text-gray-700">Loading profiles...</p>
      </div>
    );
  }

  if (profileError && !isAddingProfile && !isSetUsernameModalOpen && !showDeleteConfirmModal && !isDeleting) {
    return <ErrorMessage message={profileError} />;
  }

  // Calculate if add profile button should be enabled
  const canAddProfile = Array.isArray(profiles) && profiles.length < MAX_PROFILES_PER_USER;

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      {/* Header section removed */}

      {/* Main content area */}
      <div className="flex-grow flex items-center justify-center p-4 sm:p-6 md:p-8">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl border border-gray-200 overflow-hidden">
          {/* Panel header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between sm:items-center">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Select Your Profile</h2>
              <p className="text-gray-600 text-sm mt-1">Choose a profile to start training or create a new one</p>
            </div>

            {Array.isArray(profiles) && profiles.length > 0 && (
              <button
                onClick={() => setIsEditMode(prev => !prev)}
                className={`mt-4 sm:mt-0 flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  isEditMode
                    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <FontAwesomeIcon icon={isEditMode ? faTimes : faEdit} className="mr-2" />
                {isEditMode ? 'Exit Edit Mode' : 'Edit Profiles'}
              </button>
            )}
          </div>

          {/* Profile grid or empty state */}
          <div className="p-6">
            {Array.isArray(profiles) && profiles.length > 0 ? (
              <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 ${isEditMode ? 'opacity-95' : ''}`}>
                {profiles.map((profile) => (
                  <ProfileCard
                    key={profile.id}
                    profile={profile}
                    onSelect={handleSelectProfile}
                    isSelected={profile.id === activeProfileId}
                    isEditMode={isEditMode}
                    onDeleteRequest={handleDeleteRequest}
                  />
                ))}
              </div>
            ) : (
              !showAddForm && (
                <div className="text-center py-12">
                  <div className="bg-gray-100 inline-flex items-center justify-center w-20 h-20 rounded-full mb-4">
                    <FontAwesomeIcon icon={faUserPlus} className="text-gray-400 text-2xl" />
                  </div>
                  <h3 className="text-xl font-medium text-gray-700 mb-2">No profiles found</h3>
                  <p className="text-gray-500 mb-6 max-w-md mx-auto">Create a profile to start training your working memory with the Dual N-Back task.</p>
                  <button
                    onClick={() => { setShowAddForm(true); setAddProfileError(null); setUsernameValidationError(''); setUsernameAvailability(null); setNewUsername(''); }}
                    className="px-5 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center mx-auto"
                  >
                    <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
                    Create New Profile
                  </button>
                </div>
              )
            )}

            {/* Add profile section */}
            {!isEditMode && (
              <div className={`mt-8 pt-6 ${profiles.length > 0 ? 'border-t border-gray-200' : ''}`}>
                {canAddProfile ? (
                  <>
                    {!showAddForm ? (
                      <div className="text-center">
                        <button
                          onClick={() => { setShowAddForm(true); setAddProfileError(null); setUsernameValidationError(''); setUsernameAvailability(null); setNewUsername(''); }}
                          className="px-5 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center mx-auto"
                        >
                          <FontAwesomeIcon icon={faUserPlus} className="mr-2" />
                          {profiles.length > 0 ? 'Add Another Profile' : 'Create New Profile'}
                        </button>
                        <p className="text-xs text-gray-500 mt-2">
                          ({profiles.length}/{MAX_PROFILES_PER_USER} profiles used)
                        </p>
                      </div>
                    ) : (
                      <div className="max-w-lg mx-auto bg-white rounded-lg p-6 border border-gray-200 shadow-sm">
                        <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                          <FontAwesomeIcon icon={faUserPlus} className="mr-2 text-indigo-600" />
                          Create New Profile
                        </h3>

                        <form onSubmit={handleAddProfileSubmit} className="space-y-4">
                          <div>
                            <label htmlFor="newUsername" className="block text-sm font-medium text-gray-700 mb-1">
                              Choose a Username
                            </label>
                            <div className="relative">
                              <input
                                type="text"
                                id="newUsername"
                                value={newUsername}
                                onChange={(e) => setNewUsername(e.target.value)}
                                placeholder="e.g., PlayerOne_123"
                                required
                                maxLength={25}
                                className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                                  usernameValidationError || (usernameAvailability === 'taken')
                                    ? 'border-red-300 bg-red-50'
                                    : (usernameAvailability === 'available'
                                      ? 'border-green-300 bg-green-50'
                                      : 'border-gray-300')
                                }`}
                                aria-label="Username for new profile"
                                aria-describedby="username-constraints username-feedback add-profile-error"
                                disabled={isAddingProfile}
                              />

                              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                {isCheckingUsername && <LoadingSpinner small={true} />}
                                {!isCheckingUsername && usernameAvailability === 'available' && (
                                  <FontAwesomeIcon icon={faCheck} className="text-green-500" />
                                )}
                                {!isCheckingUsername && usernameAvailability === 'taken' && (
                                  <FontAwesomeIcon icon={faTimes} className="text-red-500" />
                                )}
                                {!isCheckingUsername && usernameAvailability === 'error' && (
                                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-yellow-500" />
                                )}
                              </div>
                            </div>

                            {/* Username status messages */}
                            {isCheckingUsername && (
                              <p className="mt-1 text-sm text-gray-500">Checking availability...</p>
                            )}
                            {!isCheckingUsername && usernameAvailability === 'available' && (
                              <p className="mt-1 text-sm text-green-600">Username is available!</p>
                            )}
                            {!isCheckingUsername && usernameAvailability === 'taken' && (
                              <p className="mt-1 text-sm text-red-600">Username is already taken.</p>
                            )}
                            {!isCheckingUsername && usernameAvailability === 'error' && (
                              <p className="mt-1 text-sm text-yellow-600">Couldn't verify availability. Try again.</p>
                            )}
                          </div>

                          <div className="bg-indigo-50 p-3 rounded-md border border-indigo-100">
                            <div className="flex items-start">
                              <FontAwesomeIcon icon={faInfoCircle} className="text-indigo-600 mt-0.5 mr-2 flex-shrink-0" />
                              <p className="text-xs text-indigo-800">
                                Username must be 3-25 characters using only letters, numbers, underscores (_), and dashes (-).
                                <strong className="font-medium"> Usernames cannot be changed later</strong> and are visible to other users.
                              </p>
                            </div>
                          </div>

                          {usernameValidationError && (
                            <p id="username-feedback" className="text-sm text-red-600" role="alert">
                              {usernameValidationError}
                            </p>
                          )}

                          {addProfileError && (
                            <div className="bg-red-50 border-l-4 border-red-400 p-3">
                              <p id="add-profile-error" className="text-sm text-red-700" role="alert">
                                {addProfileError}
                              </p>
                            </div>
                          )}

                          <div className="flex justify-end space-x-3 pt-2">
                            <button
                              type="button"
                              onClick={() => { setShowAddForm(false); setAddProfileError(null); setUsernameValidationError(''); setNewUsername(''); setUsernameAvailability(null); }}
                              disabled={isAddingProfile}
                              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm shadow-sm disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
                            >
                              Cancel
                            </button>

                            <button
                              type="submit"
                              disabled={isAddingProfile || !newUsername.trim() || !!usernameValidationError || isCheckingUsername || usernameAvailability !== 'available'}
                              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 text-sm shadow-sm disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center"
                            >
                              {isAddingProfile ? (
                                <>
                                  <LoadingSpinner small={true} />
                                  <span className="ml-2">Creating...</span>
                                </>
                              ) : (
                                <>
                                  <FontAwesomeIcon icon={faArrowRight} className="mr-2" />
                                  Create Profile
                                </>
                              )}
                            </button>
                          </div>
                        </form>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                    <div className="flex items-center justify-center mb-2">
                      <FontAwesomeIcon icon={faInfoCircle} className="text-gray-500 mr-2" />
                      <span className="text-gray-700 font-medium">Maximum profiles reached</span>
                    </div>
                    <p className="text-sm text-gray-500">
                      You've created {MAX_PROFILES_PER_USER} profiles, which is the maximum allowed.
                      Please select an existing profile or delete one to create a new one.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer removed */}

      {/* Modals */}
      <SetUsernameModal
        isOpen={isSetUsernameModalOpen}
        onClose={handleCloseSetUsernameModal}
        onSave={handleSaveMigratedUsername}
        profileData={profileToMigrateData}
        externalError={profileError}
        validateUsername={validateUsername}
        checkUsernameUrl={checkUsernameUrl}
        debounceDelay={DEBOUNCE_DELAY}
      />

      <ConfirmationModal
        isOpen={showDeleteConfirmModal}
        title="Delete Profile"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        confirmText={isDeleting ? "Deleting..." : "Delete Profile"}
        confirmColor="red"
        requiredInputText={profileToDeleteName}
        inputLabel={`Type "${profileToDeleteName}" to confirm`}
        inputValue={deleteConfirmInput}
        onInputChange={(e) => setDeleteConfirmInput(e.target.value)}
        confirmationPlaceholder={profileToDeleteName}
      >
        <div className="space-y-4">
          <div className="flex justify-center">
            <div className="h-14 w-14 rounded-full bg-red-100 flex items-center justify-center text-red-600">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-xl" />
            </div>
          </div>

          <p className="text-gray-700">
            This action <strong>cannot be undone</strong>. All data associated with profile <strong className="text-red-600">{profileToDeleteName}</strong> will be permanently deleted, including:
          </p>

          <ul className="text-sm text-gray-600 space-y-1 pl-5 list-disc">
            <li>Game progress and difficulty levels</li>
            <li>Training history and statistics</li>
            <li>Journal entries and saved replays</li>
            <li>Friend connections and leaderboard rankings</li>
          </ul>

          <div className="bg-amber-50 border-l-4 border-amber-400 p-3 text-sm text-amber-800">
            <p className="font-medium">To confirm deletion, please type the profile username below:</p>
          </div>
        </div>
      </ConfirmationModal>
    </div>
  );
};

export default ProfileSelectionScreen;